/* global Vue, VueI18n, primevue, SINGLE_OFFER, useOffers, PROPERTY_LIST_URL, BOOKING_APP_URI, PREFIX */

import OfferFilters from './components/OfferFilters'
import PropertyCard from './components/PropertyCard'
import RecommendedOffers from './components/RecommendedOffers'
import { messagesSpecific } from '../i18n/translations'
import { CreateI18n } from '../i18n/create-i18n'
import AuthService from '../AuthService'
import { BookingParams } from '../classes/BookingParams'
import { formatDateForMaistra } from '../utils-common'

const i18n = CreateI18n(messagesSpecific)

const OfferSingleApp = Vue.createApp({
  name: 'OfferSingleApp',
  components: { OfferFilters, PropertyCard, RecommendedOffers },
  template: `
<div v-if="error" class="error container">
  <h3>{{ $t('error') }}</h3>
  <p>{{ error }}</p>
</div>

<div v-else>
  <OfferFilters
    v-if="variationsLoaded && !hideFilters"
    :isForVariations="true"
    :properties="properties"
    :availableProperties="availableProperties"
    :offers="offers"
    @offerParamsChanged="offerParamsChanged"
    @error="offerParamsError" />

  <template v-if="variationsLoaded && filteredVariations.length == 0">
    <div class="container grid">
      <div class="col-12 col-sm-10 offset-sm-1 col-lg-8 offset-lg-2">
        <hr>
        <h3 class="special-offers__warning">{{ $t('offers.titleNoAvailableVariants') }}</h3>
        <hr>
      </div>
    </div>
    <RecommendedOffers :offers="offers" />
  </template>

  <div v-else class="container">
    <div v-for="variation in filteredVariations">
      <PropertyCard
        v-if="variation.propertyData?.phobsId"
        :key="variation.propertyData?.phobsId"
        :property="variation.propertyData"
        :bookingParams="createVariationBookingParams(variation)"
        :customDescription="generatePropertyDescription(variation)"
        :availabilityPeriods="variation.periods || null"
        :fallbackAvailability="true"
        :discount="formatDiscount(variation)"
        :hasPrice="false" />
    </div>
  </div>
</div>
  `,
  setup () {
    const { t } = VueI18n.useI18n()
    const offers = Vue.ref([])
    const offerVariations = Vue.ref([])
    const variationsLoaded = Vue.ref(false)
    const properties = Vue.ref([])
    const error = Vue.ref('')
    const accountId = Vue.ref('')
    const hideFilters = Vue.ref(false)

    function generatePropertyDescription (variation) {
      const nightsText = `${t('offers.minimumStayPrefix')} ${variation.minNights || 1}`
      let periodText = ''
      if (variation.periodsText) {
        periodText = `${t('offers.period')} ${variation.periodsText}`
      } else if (variation.periods) {
        const periods = variation.periods.map(p => {
          const startDate = new Date(p.start_date)
          const endDate = new Date(p.end_date)
          const today = new Date()
          return endDate >= today ? `${formatDateForMaistra(startDate)} - ${formatDateForMaistra(endDate)}` : null
        }).filter(p => p !== null)
        periodText = `${t('offers.period')} ${periods.join(' | ')}`
      } else if (variation.from && variation.to) {
        periodText = `${t('offers.period')} ${variation.from} - ${variation.to}`
      }
      return `${nightsText}<br>${periodText}`
    }

    function propertySlug (variation, type) {
      const slug = variation.property.replace('/_index', '')
      if (type === 'property' && variation.campingType) {
        return slug + `-${variation.campingType}`
      }
      if (type === 'bundle' && variation?.propertyData?.bundle) {
        return variation.propertyData.bundle.replace('/_index', '')
      }
      return slug
    }

    function isVariationValid (variation) {
      return !filter.properties || (
        variation.property &&
        filter.properties.filter(p => propertySlug(variation, 'property') === p).length
      )
    }

    const filter = Vue.reactive({
      properties: null,
    })

    const filteredVariations = Vue.computed(() => offerVariations.value.filter(isVariationValid))

    const availableProperties = Vue.computed(() => Array.from(
      offerVariations.value.reduce((acc, variation) => {
        acc.add(propertySlug(variation))
        acc.add(propertySlug(variation, 'property'))
        acc.add(propertySlug(variation, 'bundle'))
        return acc
      }, new Set()),
    ))

    async function offerParamsChanged (filters) {
      Object.assign(filter, filters)
    }

    function twoPlaceNumber (num) {
      return num > 9 ? num : '0' + num
    }

    function includePropertyData () {
      offerVariations.value = offerVariations.value.map(variation => {
        const subProperties = properties.value.filter(p => variation.property.replace('/_index', '') === p.propertyGroup) || []

        if (subProperties.length > 1) {
          variation.propertyData = subProperties.find(p => variation.campingType === p.campingType)
        } else if (subProperties.length === 1) {
          variation.propertyData = subProperties[0]
        } else {
          variation.propertyData = {}
        }
        const from = new Date(variation.from)
        const expiryDate = new Date(variation.expiryDate)
        variation.from = `${twoPlaceNumber(from.getUTCDate())}.${twoPlaceNumber(from.getUTCMonth() + 1)}.${from.getUTCFullYear()}`
        variation.to = `${twoPlaceNumber(expiryDate.getUTCDate())}.${twoPlaceNumber(expiryDate.getUTCMonth() + 1)}.${expiryDate.getUTCFullYear()}`
        return variation
      })
      variationsLoaded.value = true
    }

    async function getProperties () {
      const response = await fetch(PROPERTY_LIST_URL)
      if (!response.ok) {
        throw new Error(response.statusText)
      }
      properties.value = await response.json()
      includePropertyData()
    }

    function offerParamsError (err) {
      console.error(err)
      error.value = err
    }

    function createVariationBookingParams (variation) {
      const bookingParams = Vue.reactive(new BookingParams())

      const dateFromSplit = variation.from.split('.')
      const startDate = new Date(`${dateFromSplit[2]}-${dateFromSplit[1]}-${dateFromSplit[0]}`)
      const firstBookableDate = new Date()
      if (variation.min_days_to_booking) {
        firstBookableDate.setDate(firstBookableDate.getDate() + variation.min_days_to_booking)
      }
      bookingParams.startDate = startDate.getTime() < firstBookableDate.getTime() ? firstBookableDate : startDate

      if (variation.minNights) {
        bookingParams.nights = variation.minNights
      }

      if (variation.accessCode) {
        bookingParams.accessCode = variation.accessCode
      }
      if (variation.ratePlanId) {
        bookingParams.ratePlanId = variation.ratePlanId
      }
      if (accountId.value) {
        bookingParams.accountId = accountId.value
      }

      return Vue.reactive(bookingParams)
    }

    function formatDiscount (variation) {
      // TODO: remove once all offers have discount / price set as numeric values
      if (variation.discount && isNaN(variation.discount) && variation.discount.length > 1) {
        return variation.discount
      }

      if (variation.price) {
        return `${t('offers.from')} ${variation.price} €`
      }

      return `${t('offers.upTo')} ${variation.discount} %`
    }

    async function getOfferVariations () {
      offers.value = await useOffers()

      const offer = offers.value.find(offer => offer.slug === SINGLE_OFFER.slug)
      if (offer) {
        offerVariations.value = offer.variations
      }
    }

    Vue.onMounted(async () => {
      AuthService.on('authenticated', e => {
        accountId.value = e.detail.accountId
      })

      await getOfferVariations()
      await getProperties()

      if (filteredVariations.value.length === 1) {
        hideFilters.value = true
        const url = new URL(window.location.href)
        if (url.hash === '#/#variations') {
          console.log(filteredVariations.value[0])
          window.location.href = `${BOOKING_APP_URI}${PREFIX}/step1?propertyId=${filteredVariations.value[0].propertyData.phobsId}&ratePlanId=${filteredVariations.value[0].ratePlanId}`
        }
      }
    })

    return {
      error,
      hideFilters,
      filteredVariations,
      variationsLoaded,
      properties,
      availableProperties,
      generatePropertyDescription,
      offerParamsChanged,
      offerParamsError,
      createVariationBookingParams,
      formatDiscount,
      offers,
    }
  },
})

const appElement = document.getElementById('offer-variations-app')

if (appElement) {
  OfferSingleApp.use(i18n)
  OfferSingleApp.use(primevue)
  OfferSingleApp.mount(appElement)
}
